import type { UseFormReturnType } from '@mantine/form';
import { getLogger } from 'loglevel';
import type { ReactElement, ReactNode } from 'react';

import type { AllKeysAreStrings } from '@apple/utils/type';
import type { FormErrors } from '@apple/utils/validation/models';

import { type UseValidatedFormProps, useValidatedForm } from '../../hooks/use-validated-form';
import { FormError } from '../FormError';
import type { SubmitButtonProps } from '../SubmitButton';
import { SubmitButton } from '../SubmitButton';

const log = getLogger('validation');

export type ValidatedFormProps<
	T extends AllKeysAreStrings<T>,
	TTransformedValues = T,
> = UseValidatedFormProps<T, TTransformedValues> & {
	children: (props: {
		isSubmitting: boolean;
		hasSubmitted: boolean;
		form: UseFormReturnType<T, TTransformedValues>;
		formErrors: FormErrors | undefined;
		submit: () => void;
		renderFormErrors: (excludeFieldErrors?: boolean) => ReactElement;
		renderSubmitButton: (
			props: Omit<SubmitButtonProps, 'isSubmitting' | 'form'>,
		) => ReactElement<SubmitButtonProps>;
	}) => ReactNode;
};

export function ValidatedForm<T extends AllKeysAreStrings<T>, TTransformedValues = T>({
	children,
	...useFormProps
}: ValidatedFormProps<T, TTransformedValues>) {
	const { form, handleSubmit, isSubmitting, hasSubmitted, formErrors } = useValidatedForm<
		T,
		TTransformedValues
	>(useFormProps);

	return (
		<form
			onSubmit={form.onSubmit(
				(values, e) => {
					log.info(`Submitting '${useFormProps.name}' form`);
					// Prevents interference with child forms
					e?.stopPropagation();
					void handleSubmit(values);
				},
				(errors, values) => {
					log.warn(`Form '${useFormProps.name}' has errors:`, errors, values);
				},
			)}
		>
			{children({
				isSubmitting,
				hasSubmitted,
				form,
				formErrors,
				submit: () => {
					log.info(`Submitting '${useFormProps.name}' form`);
					form.onSubmit(
						values => {
							log.info(`Form '${useFormProps.name}' validation passed`);
							void handleSubmit(values);
						},
						(errors, values) => {
							log.warn(`Form '${useFormProps.name}' has errors:`, errors, values);
						},
					)();
				},
				renderFormErrors: (excludeFieldErrors = false) => (
					<FormError errors={formErrors ?? (excludeFieldErrors ? [] : form.errors)} />
				),
				renderSubmitButton: props => (
					<SubmitButton
						{...props}
						form={form as UseFormReturnType<unknown>}
						isSubmitting={isSubmitting}
					/>
				),
			})}
		</form>
	);
}
